import { DocumentOrganizationKeys, OrderDirection } from '@griply/types';
import {
  groupByModeTranslationKeys,
  sortingModeTranslationKeys,
} from '@griply/utils';
import { TFunction } from 'i18next';
import { Layers, Sort } from 'shared/assets/icons';
import { Option } from 'shared/components/ui/custom-form-select';
import {
  FilterGroup,
  FilterOption,
  FilterOptionType,
} from 'shared/types/filter';
import {
  GoalSortingMode,
  GroupByMode,
  SortingMode,
} from 'shared/types/sorting-mode';

const DEFAULT_SORT_ORDER: readonly SortingMode[] = [
  SortingMode.Custom,
  SortingMode.DueDate,
  SortingMode.Priority,
  SortingMode.DeadlineDeadline,
  SortingMode.Name,
  SortingMode.CreatedAt,
];

const DEFAULT_GROUP_BY_ORDER: readonly GroupByMode[] = [
  GroupByMode.None,
  GroupByMode.Section,
  GroupByMode.LifeArea,
  GroupByMode.Goal,
  GroupByMode.Tag,
  GroupByMode.StartDate,
  GroupByMode.Deadline,
  GroupByMode.Priority,
  GroupByMode.DeadlineDeadline,
  GroupByMode.DayPart,
  GroupByMode.Type,
];

export type Options = {
  t: TFunction;
  supportedGroupByModes?: readonly GroupByMode[];
  supportedSortingModes?: readonly (SortingMode | GoalSortingMode)[];
  labelOverrides?: Partial<Record<GroupByMode, string>>;
  sortingTranslationKeys?: Partial<
    Record<SortingMode | GoalSortingMode, string>
  >;
  // When provided, an Ascending/Descending section is appended inside the Sort
  // dropdown (separated from the modes).
  sortingDirection?: OrderDirection;
};

export const createGroupingAndSorting = ({
  t,
  supportedSortingModes,
  supportedGroupByModes,
  labelOverrides,
  sortingTranslationKeys = sortingModeTranslationKeys,
  sortingDirection,
}: Options): FilterGroup[] => {
  const sortTranslationKeys = sortingTranslationKeys as Partial<
    Record<string, string>
  >;
  const groupTranslationKeys = groupByModeTranslationKeys as Partial<
    Record<string, string>
  >;

  const sortOptions = (supportedSortingModes ?? DEFAULT_SORT_ORDER)
    .map((value): Option | null => {
      const key = sortTranslationKeys[value];
      return key ? { label: t(key), value } : null;
    })
    .filter((option): option is Option => option !== null);

  // Append the direction options as a separated section within the Sort dropdown.
  if (sortingDirection && sortOptions.length) {
    sortOptions[sortOptions.length - 1] = {
      ...sortOptions[sortOptions.length - 1],
      hasBottomSeparator: true,
    };
    sortOptions.push(
      {
        label: t('context-menus.filtering.sort-direction.ascending'),
        value: OrderDirection.Asc,
      },
      {
        label: t('context-menus.filtering.sort-direction.descending'),
        value: OrderDirection.Desc,
      },
    );
  }

  const groupByOptions = (supportedGroupByModes ?? DEFAULT_GROUP_BY_ORDER)
    .map((value): Option | null => {
      const key = groupTranslationKeys[value];
      const label = labelOverrides?.[value] ?? (key ? t(key) : undefined);
      return label ? { label, value } : null;
    })
    .filter((option): option is Option => option !== null);

  const filters: FilterOption[] = [];

  if (sortOptions.length) {
    filters.push({
      id: DocumentOrganizationKeys.SortingMode,
      label: t('context-menus.filtering.task.grouping-and-sorting.sort.label'),
      placeholder: t('general.labels.select'),
      icon: Sort,
      type: FilterOptionType.Select,
      selectOptions: sortOptions,
    });
  }

  if (groupByOptions.length) {
    filters.push({
      id: DocumentOrganizationKeys.GroupByMode,
      label: t(
        'context-menus.filtering.task.grouping-and-sorting.grouping.label',
      ),
      placeholder: t('general.labels.select'),
      icon: Layers,
      type: FilterOptionType.Select,
      selectOptions: groupByOptions,
    });
  }

  if (!filters.length) {
    return [];
  }

  return [
    {
      id: 'groupingAndSorting',
      title: t('context-menus.filtering.task.grouping-and-sorting.title'),
      filters,
    },
  ];
};
